import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Person } from "@/Common/Models/Person/Person";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { RAMQLieuDeSuivi } from "@/Common/Models/RAMQ/RAMQLieuDeSuivi";
import { TimeSpan } from "@/Common/Models/TimeSpan";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { DiagnosisResponse } from "@/Patient/Api/MedfarBilling/Dto/Responses/DiagnosisResponse";
import { FacturationCodeDTO, FacturationDiagnoticDTO, FacturationDTO, FacturationPatientDTO, FacturationRAMQinfoDTO, FacturationRepresentantDTO, FacturationTeleconferenceType, FacturationVulnerabilityCodesDTO } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { OtherCodeResponse } from "@/Patient/Api/MedfarBilling/Dto/Responses/OtherCodeResponse";
import { MedfarBilling as MyleComponentsMedfarBilling, MedfarBillingState } from "@/Patient/Components/MedfarBilling/MedfarBilling";
import { IMedfarBillingCustomCodeItem } from "@/Patient/Components/MedfarBilling/MedfarBillingCustomCodeBox";
import { FacturationCodeHelper } from "@/Patient/Helpers/MedfarBilling/FacturationCodeHelper";
import { ContextElementClass } from "@/Patient/Models/MedfarBilling/ContextElementClass";
import { ErrorFacturation, FacturationCode } from "@/Patient/Models/MedfarBilling/FacturationCode";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { IMedfarBillingModel } from "@/Patient/Models/UI/MedfarBilling/IMedfarBillingModel";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { PatientStore } from "@/Patient/Stores/PatientStore";

class MedfarBillingHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalPatientOperations.MedfarBilling.PrepareNewBill, this.prepareNewBill);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.CreateNewBill, this.createNewBill);

        this.RegisterOperation(LocalPatientOperations.MedfarBilling.ClosePopup, this.closePopup);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.CommentsChange, this.changeComments);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.ChangeDate, this.changeDate);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.TodayClick, this.clickToday);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.HoursChange, this.changeHours);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.DiagnosisChange, this.changeDiagnosis);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.AddDiagnosisToList, this.addDiagnosisToList);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.RemoveDiagnosisFromList, this.removeDiagnosisFromList);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.AddCodeToList, this.addCodeToList);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.RemoveCodeToList, this.removeCodeFromList);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnSrvClick, this.setSrv);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnLessThan500HeaderClick, this.setLessThan500Patient);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnMoreThan500PatientClick, this.setMoreThan500Patient);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnMoreThan1000PatientClick, this.setMoreThan1000Patient);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnMoreThan1500PatientClick, this.setMoreThan1500Patient);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxClickRdv, this.setRdv);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxClickGrossesse, this.setPregnancy);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxVADomicileClick, this.setVADomicile);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxSrvNonInscritClick, this.setSrvNonInscrit);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxAutreClick, this.setAutre);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnEvalReferreCheckboxClick, this.setEvalReferre);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxCsstClick, this.setCnesst);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCancelClick, this.cancel);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.Referrals_OnClear, this.clearReferrals);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.Referrals_OnChange, this.changeReferrals);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.Referrals_OnTextChange, this.changeReferralsText);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.SetDateGrossesse, this.setPregnancyDate);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.SetDateCsst, this.setCnesstDate);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.TodayClickGrossesse, this.setPregnancyToday);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.TodayClickCSST, this.setCnesstToday);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnContinueClick, this.continue);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.RemoveRefDoctor, this.removeRefDoctor);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.ChangeContextAutres, this.changeContextAutres);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnPsychoRadioClick, this.setPsycho);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnIntClinClick, this.setIntClin);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.CommSpec_OnClear, this.clearCommSpec);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.CommSpec_OnChange, this.changeCommSpec);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.CommSpec_OnTextChange, this.changeCommSpecText);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.RemoveCommSpecDoctor, this.removeCommSpecDoctor);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxCommunicationClick, this.setCommunication);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCommProfFirstNameChange, this.changeCommProfFirstName);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCommProfLastNameChange, this.changeCommProfLastName);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCommProfTypeSelection, this.changeCommProfType);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCommProfOutQuebecClick, this.setCommProfOutQuebec);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCommProfQuebecClick, this.setOnCommProfQuebec);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.RemoveCommProfDoctor, this.removeCommProfDoctor);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnPecRadioCheck, this.setPecRadio);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.AddFavoriteClick, this.addFavorite);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCancelFavoriteClick, this.cancelFavorite);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnAddNewFavoriteClick, this.addNewFavorite);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnMesure1TextChange, this.changeMesure1Text);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnMesure2TextChange, this.changeMesure2Text);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnFavoriteRemove, this.removeFavorite);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnCheckboxFavoriteClick, this.setFavorite);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.GenerateBill, this.generateBill);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OnSrvOrRdvCancelClick, this.cancelSrvOrRdv);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.SceanceDiffClick, this.clickSceanceDiff);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.ConferenceCallClick, this.setIsConferenceCall);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.ConferenceTypeSet, this.setConferenceType);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.OpenExistingBill, this.openExistingBill);
    }

    private openExistingBill(request: LocalEvent, store: PatientStore): void {
        const selectedFacturationRequestId = request.Get<string>("billId");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        let selectedBill = noteModel.get("EncounterMedfarBills").filter(x => x.FacturationRequestID == selectedFacturationRequestId).firstOrDefault();
        if (selectedBill != null) {
            noteModel = noteModel.set("ShowNewMedfarBillPopup", true);

            // give priority to the encounter site if its clinical code matches the bill's clinical code
            let validSites = cacheStore.SiteCache.filter(x => x.Model.ClinicalCode == selectedBill.CodeDeCabinet);
            let site = validSites.firstOrDefault(x => x.Model.Id == noteModel.get("Encounter").SiteId);
            if (site == null)
                site = validSites.firstOrDefault();

            let siteId = site == null ? null : site.Model.Id;

            let medfarBillingModel = MyleComponentsMedfarBilling.getDefaultMedfarBillingModel();
            let medfarBillingInitialState = MyleComponentsMedfarBilling.GetDefaultState();

            medfarBillingModel = this.initializeCodesList(medfarBillingModel);

            medfarBillingModel = medfarBillingModel
                .set("Model", selectedBill)
                .set("SiteId", siteId)
                .set("IsUpdating", true)
                .set("IsMoreThan500", selectedBill.Has500Patient == true)
                .set("IsLessThan500", selectedBill.Has500Patient != true)
                .set("IsSRVChecked", selectedBill.IsSansRendezVous == true)
                .set("IsRDVChecked", selectedBill.IsSansRendezVous != true)
                .set("IsConferenceCallChecked", selectedBill.IsConferenceCall)
                .set("TeleconferenceType", selectedBill.TeleconferenceType);

            if (selectedBill.DateFacturation != null)
                medfarBillingModel = medfarBillingModel.set("BillDate", moment(selectedBill.DateFacturation).toDate());
            else if (selectedBill.FacturationCodes && selectedBill.FacturationCodes.length > 0)
                medfarBillingModel = medfarBillingModel.set("BillDate", moment(selectedBill.FacturationCodes[0].DateFacturation).toDate());

            if (selectedBill.FacturationCodes) // Seance Differene
                medfarBillingModel = medfarBillingModel.set("SceanceDiffChecked", selectedBill.FacturationCodes.filter(f => f.ElementsdeContext != null).some(f => f.ElementsdeContext.some(e => e === "85")));

            let HasMDFam = selectedBill.PatientDTO.RamqInfo && selectedBill.PatientDTO.RamqInfo.MDFamilleLicenceNumber != "";
            medfarBillingModel = medfarBillingModel
                .set("IsPatientNotSubscribed", !HasMDFam)
                .set("IsPatientSubscribed", HasMDFam);

            if (HasMDFam && selectedBill.PatientDTO.RamqInfo.VulnerabilityCodes && selectedBill.PatientDTO.RamqInfo.VulnerabilityCodes.length > 0)
                medfarBillingModel = medfarBillingModel.set("IsVulnerable", true);

            if (selectedBill.PatientDTO.CommentaireDuMedecin)
                medfarBillingModel = medfarBillingModel.set("Comments", selectedBill.PatientDTO.CommentaireDuMedecin);

            let rdvPatientInscritItem = medfarBillingModel.get("RdvPatientInscritItem");
            let grossesseItems = medfarBillingModel.get("GrossesseItems");
            let domicilesItems = medfarBillingModel.get("DomicilesItems");
            let srvNonInscritItem = medfarBillingModel.get("SrvNonInscritItem");
            let autresItems = medfarBillingModel.get("AutresItems");
            let evaluationRefereeItems = medfarBillingModel.get("EvaluationRefereeItems");
            let csstItems = medfarBillingModel.get("CSSTItems");
            let communicationsItems = medfarBillingModel.get("CommunicationsItems");

            let facturationCode = new FacturationCodeHelper(true);
            let CodeGroupDictionaryByFinalCode: { [finalCode: number]: FacturationCode } = {};
            Object.keys(facturationCode.codeGroups).forEach(
                codeGroupKey =>
                    facturationCode.codeGroups[codeGroupKey].GetCombines().forEach(
                        finalCode =>
                            CodeGroupDictionaryByFinalCode[finalCode] = facturationCode.codeGroups[codeGroupKey]
                    )
            );

            const codeList: FacturationCode[] = [];
            if (selectedBill.FacturationCodes) {
                selectedBill.FacturationCodes.forEach(code => {
                    const facCode = FacturationCode.FromFacturationCodeDTO(code, CodeGroupDictionaryByFinalCode);

                    if (facturationCode.isIntervention(facCode.CodeGroupIdentifier)) {
                        medfarBillingInitialState.isInterventionCliniqueChecked = true;
                        medfarBillingInitialState.InterventionCliniqueDurationMin = IsNullOrWhiteSpace(medfarBillingInitialState.InterventionCliniqueDurationMin) ? "25" : medfarBillingInitialState.InterventionCliniqueDurationMin;

                        if (code.ElementsdeContext != null) {
                            const Context = code.ElementsdeContext.filter(x => x == "1713" || x == "1714" || x == "1715" || x == "1716");
                            if (Context.length > 0)
                                medfarBillingModel = medfarBillingModel.set("SelectedContextCodeString", Context[0]);
                        }

                        if (facturationCode.isInterventionSupp(facCode.CodeGroupIdentifier) == true) {
                            medfarBillingInitialState.InterventionCliniqueDurationMin = (Number(facCode.Mesure1) + 30).toString();
                        }
                    }

                    if (!IsNullOrWhiteSpace(code.LicenceMedecinReferant) && code.LicenceMedecinReferant.length > 0 && code.LicenceMedecinReferant.substr(0, 1) == "R" && facturationCode.requiresReferringDoctor(facCode.CodeGroupIdentifier) != true) {
                        medfarBillingInitialState.AvecResident = true;
                        medfarBillingInitialState.AvecResidentLicence = code.LicenceMedecinReferant;
                        medfarBillingInitialState.AvecResidentNom = code.NomMedecinReferant;

                        const currentResident = RAMQInfo.GetCurrentResident(code.LicenceMedecinReferant);
                        if (currentResident) {
                            medfarBillingInitialState.withResidentCurrentSelection = currentResident;
                        }
                    }

                    medfarBillingInitialState.MDTraitantEnCongeProlonge = code.ElementsdeContext?.contains("1712");

                    Object.keys(csstItems).map(key => {
                        if (csstItems[key].Description === code.Description) {
                            csstItems[key].IsChecked = true;
                            facCode.DateCSST = moment(selectedBill.PatientDTO.DateEvenement).toDate();

                            medfarBillingModel = medfarBillingModel
                                .set("DateCSST", moment(selectedBill.PatientDTO.DateEvenement).toDate())
                                .set("TempDateCSST", moment(selectedBill.PatientDTO.DateEvenement).toDate());
                        }
                    });

                    Object.keys(grossesseItems).map(key => {
                        if (grossesseItems[key].Description === code.Description) {
                            grossesseItems[key].IsChecked = true;

                            if (code.NomMedecinReferant != "") {
                                facCode.NomMedecinReferant = code.NomMedecinReferant;
                            }
                            if (code.LicenceMedecinReferant != "") {
                                facCode.LicenceMedecinReferant = code.LicenceMedecinReferant;
                            }

                            if (grossesseItems[key].Description != "Suivi grossesse") {
                                facCode.DateAccouchement = moment(selectedBill.PatientDTO.DateEvenement).toDate();

                                medfarBillingModel = medfarBillingModel
                                    .set("DatePrevuAccouchement", moment(selectedBill.PatientDTO.DateEvenement).toDate())
                                    .set("TempDatePrevuAccouchement", moment(selectedBill.PatientDTO.DateEvenement).toDate());
                            }

                            if (facturationCode.requiresReferringDoctor(facCode.CodeGroupIdentifier) == true && facCode.LicenceMedecinReferant && facCode.NomMedecinReferant) {
                                const doc = new Person();
                                doc.Name = facCode.NomMedecinReferant;
                                doc.Licence = facCode.LicenceMedecinReferant;
                                medfarBillingModel = medfarBillingModel.set("MedecinReferant", doc);
                            }
                        }
                    });

                    Object.keys(rdvPatientInscritItem).map(key => {
                        if (rdvPatientInscritItem[key].Description === code.Description) {
                            rdvPatientInscritItem[key].IsChecked = true;
                        }
                    });

                    Object.keys(domicilesItems).map(key => {
                        if (domicilesItems[key].Description === code.Description) {
                            domicilesItems[key].IsChecked = true;
                        }
                    });

                    Object.keys(srvNonInscritItem).map(key => {
                        if (srvNonInscritItem[key].Description === code.Description) {
                            srvNonInscritItem[key].IsChecked = true;
                        }
                    });

                    Object.keys(autresItems).map(key => {
                        if (autresItems[key].Description === code.Description) {
                            autresItems[key].IsChecked = true;
                        }
                    });

                    Object.keys(evaluationRefereeItems).map(key => {
                        if (evaluationRefereeItems[key].Description === code.Description) {
                            evaluationRefereeItems[key].IsChecked = true;

                            facCode.NomMedecinReferant = code.NomMedecinReferant;
                            facCode.LicenceMedecinReferant = code.LicenceMedecinReferant;

                            if (facCode.LicenceMedecinReferant && facCode.NomMedecinReferant) {
                                const doc = new Person();

                                doc.Name = facCode.NomMedecinReferant;
                                doc.Licence = facCode.LicenceMedecinReferant;

                                medfarBillingModel = medfarBillingModel.set("MedecinReferant", doc);
                            }
                        }
                    });

                    Object.keys(communicationsItems).map(key => {
                        if (communicationsItems[key].Description === code.Description) {
                            communicationsItems[key].IsChecked = true;

                            if (communicationsItems[key].CodeGroupIdentifier === "Communications--Specialiste") {
                                const doc = new Person();

                                doc.Name = code.NomMedecinReferant;
                                doc.Licence = code.LicenceMedecinReferant;

                                medfarBillingModel = medfarBillingModel.set("CommSpecDoctor", doc);

                                facCode.NomMedecinReferant = code.NomMedecinReferant;
                                facCode.LicenceMedecinReferant = code.LicenceMedecinReferant;
                            }

                            if (communicationsItems[key].CodeGroupIdentifier === "Communications--Profressionnel") {
                                facCode.NomMedecinComm = code.NomMedecinComm;
                                facCode.PrenomMedecinComm = code.PrenomMedecinComm;
                                facCode.LicenceMedecinComm = code.LicenceMedecinComm;
                                facCode.ProvinceMedecinComm = code.ProvinceMedecinComm;
                                facCode.TypeMEdecinComm = code.TypeMedecinComm;

                                let description: string = null;
                                switch (facCode.TypeMEdecinComm) {
                                    case "1":
                                        description = "Médecin";
                                        break;

                                    case "2":
                                        description = "Optométriste";
                                        break;

                                    case "3":
                                        description = "Infirmière praticien spécialisée";
                                        break;

                                    case "4":
                                        description = "Travialleur sociale";
                                        break;

                                    case "5":
                                        description = "Ergothérapeute";
                                        break;

                                    case "6":
                                        description = "Sage-Femme";
                                        break;

                                    case "7":
                                        description = "Physiohérapeute";
                                        break;

                                    case "8":
                                        description = "Psychologue";
                                        break;

                                    case "9":
                                        description = "Audiologiste";
                                        break;

                                    case "10":
                                        description = "Audioprothésiste";
                                        break;

                                    case "11":
                                        description = "Coroner";
                                        break;

                                    case "12":
                                        description = "Médecin de la commission";
                                        break;

                                    case "13":
                                        description = "Orthophoniste";
                                        break;

                                    case "14":
                                        description = "Pharmacien";
                                        break;

                                    case "15":
                                        description = "Médecin omnipracticien";
                                        break;

                                    case "16":
                                        description = "Médecin spécialiste";
                                        break;

                                    case "17":
                                        description = "Résident";
                                        break;

                                    case "18":
                                        description = "Dentiste";
                                        break;

                                    case "99":
                                        description = "Autre";
                                        break;
                                }

                                if (description)
                                    medfarBillingModel = medfarBillingModel.set("CommProfTypeSelected", new ContextElementClass(facCode.TypeMEdecinComm, description));

                                medfarBillingModel = medfarBillingModel
                                    .set("CommProfLastName", facCode.NomMedecinComm)
                                    .set("CommProfFirstName", facCode.PrenomMedecinComm)
                                    .set("ShowCommProf", true);
                            }
                        }
                    });

                    codeList.push(facCode);
                });
            }
            medfarBillingModel = medfarBillingModel.set("CodesList", Immutable.List(codeList));

            // Diag
            const diagList: DiagnosisResponse[] = [];
            if (selectedBill.PatientDTO.FacturationDiagnotics && selectedBill.PatientDTO.FacturationDiagnotics.length > 0) {
                for (let selectedDiagnostic of selectedBill.PatientDTO.FacturationDiagnotics) {
                    let diag = new DiagnosisResponse();

                    diag.CodeCIM = selectedDiagnostic.DiagCim9Code;
                    diag.DescriptionLongue = selectedDiagnostic.DiagCim9Text;

                    diagList.push(diag);
                }
            }
            medfarBillingModel = medfarBillingModel
                .set("DiagnosisList", Immutable.List(diagList));

            noteModel = noteModel
                .set("MedfarBillModel", medfarBillingModel)
                .set("MedfarBillModelInitialState", medfarBillingInitialState);
        }

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        const isSpecialist = store.PatientMedicalNoteModels[request.PatientId].get("IsMedfarBillingSpecialist");
        store.ActionsMedfarBilling.LoadFavorite(isSpecialist);
    }

    private clickSceanceDiff(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const checked = medfarBillModel.get("SceanceDiffChecked");

        medfarBillModel = medfarBillModel.set("SceanceDiffChecked", !checked);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }
    private setIsConferenceCall(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        medfarBillModel = medfarBillModel.set("IsConferenceCallChecked", !medfarBillModel.get("IsConferenceCallChecked"));
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setConferenceType(request: LocalEvent, store: PatientStore): void {
        const type = request.Get<FacturationTeleconferenceType>("value");

        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const medfarBillModel = noteModel
            .get("MedfarBillModel")
            .set("TeleconferenceType", type);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel
            .set("MedfarBillModel", medfarBillModel);
    }

    private cancelSrvOrRdv(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("NoSRVorRDVCheckedError", false);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private generateBill(request: LocalEvent, store: PatientStore): void {
        let state = request.Get<MedfarBillingState>("stateDTO");
        if (state.isInterventionCliniqueChecked == true && Number(state.InterventionCliniqueDurationMin) < 25) {
            sessionStore.showPopup("Une intervention clinique doit avoir une durée d'au moins 25 minutes.");
            return;
        }

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        let model = medfarBillModel.get("Model");
        let codeList = medfarBillModel.get("CodesList").toArray();

        if (IsNullOrWhiteSpace(medfarBillModel.get("Comments")) && codeList.length == 0) {
            sessionStore.showPopup("Vous devez envoyer au moins 1 code ou un commentaire pour générer une facture.");
            return;
        }

        const diagList = medfarBillModel.get("DiagnosisList").toArray();
        const sceanceDiff = medfarBillModel.get("SceanceDiffChecked");

        if (!medfarBillModel.get("IsSRVChecked") && !medfarBillModel.get("IsRDVChecked")) {
            medfarBillModel = medfarBillModel.set("NoSRVorRDVCheckedError", true);

            noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
            store.PatientMedicalNoteModels[request.PatientId] = noteModel;
        }
        else {
            const dtoDiagList = diagList.map(diag => {
                const dtoDiag = new FacturationDiagnoticDTO();

                dtoDiag.DiagCim9Code = diag.CodeCIM;
                dtoDiag.DiagCim9Text = diag.DescriptionLongue;

                return dtoDiag;
            });

            model.PatientDTO.FacturationDiagnotics = dtoDiagList;

            const dtoCodeList = codeList.map(code => {
                const dtoCode = new FacturationCodeDTO();

                dtoCode.DateFacturation = medfarBillModel.get("BillDate");
                dtoCode.CodeFacturation = code.Code;

                dtoCode.Description = code.Description;

                dtoCode.Mesure1 = code.Mesure1;
                dtoCode.MesureType1 = code.UnitType1;

                if (code.CodeGroupIdentifier !== "AUTRES1--InterventionClinique" && code.CodeGroupIdentifier !== "AUTRES1--InterventionClinique_supp") {
                    dtoCode.Mesure2 = code.Mesure2;
                    dtoCode.MesureType2 = code.UnitType2;
                }

                dtoCode.NomMedecinComm = code.NomMedecinComm;
                dtoCode.PrenomMedecinComm = code.PrenomMedecinComm;
                dtoCode.LicenceMedecinComm = code.LicenceMedecinComm;
                if (medfarBillModel.get("CommProfTypeSelected")) {
                    dtoCode.TypeMedecinComm = medfarBillModel.get("CommProfTypeSelected").ContextCode;
                }
                else {
                    dtoCode.TypeMedecinComm = "";
                }
                dtoCode.ProvinceMedecinComm = code.ProvinceMedecinComm;

                dtoCode.ElementsdeContext = [];
                if (sceanceDiff === true) {
                    dtoCode.ElementsdeContext.push("85");
                }

                if (medfarBillModel.get("SelectedContextCodeString") && (code.CodeGroupIdentifier === "AUTRES1--InterventionClinique" || code.CodeGroupIdentifier === "AUTRES1--InterventionClinique_supp")) {
                    dtoCode.ElementsdeContext.push(medfarBillModel.get("SelectedContextCode").ContextCode);
                }

                if (state.AvecResident == true && IsNullOrWhiteSpace(code.LicenceMedecinReferant)) { // Ignore Resident if its a communication specialiste code that has a refere. Known RAMQ issue
                    dtoCode.NomMedecinReferant = state.AvecResidentNom;
                    dtoCode.LicenceMedecinReferant = state.AvecResidentLicence;
                }
                else {
                    dtoCode.NomMedecinReferant = code.NomMedecinReferant;

                    if (!IsNullOrWhiteSpace(code.LicenceMedecinReferant) && code.LicenceMedecinReferant.length > 0 && code.LicenceMedecinReferant.substr(0, 1) != "R")
                        dtoCode.LicenceMedecinReferant = RAMQInfo.appendRamqCheckSumIfMissing(code.LicenceMedecinReferant);
                }

                if (state.MDTraitantEnCongeProlonge == true)
                    dtoCode.ElementsdeContext.push("1712");

                dtoCode.FacturationRequestID = model.FacturationRequestID;

                return dtoCode;
            });

            if (medfarBillModel.get("DatePrevuAccouchement")) {
                model.PatientDTO.DateEvenement = medfarBillModel.get("DatePrevuAccouchement");
                model.PatientDTO.TypeEvenement = "DDM";
            }

            if (medfarBillModel.get("DateCSST")) {
                model.PatientDTO.DateEvenement = medfarBillModel.get("DateCSST");
                model.PatientDTO.TypeEvenement = "CSST";
            }

            if (medfarBillModel.get("IsSRVChecked")) {
                model.IsSansRendezVous = true;
            }
            else {
                model.IsSansRendezVous = false;
            }

            model.IsConferenceCall = medfarBillModel.get("IsConferenceCallChecked") || false;
            model.TeleconferenceType = medfarBillModel.get("TeleconferenceType") || FacturationTeleconferenceType.None;

            if (medfarBillModel.get("IsLessThan500")) {
                model.Has500Patient = false;
            }
            else {
                model.Has500Patient = true;
            }

            if (medfarBillModel.get("Comments")) {
                model.PatientDTO.CommentaireDuMedecin = medfarBillModel.get("Comments");
            }

            model.FacturationCodes = dtoCodeList;

            store.ActionsMedfarBilling.GenerateBill(model);
        }

        medfarBillModel = medfarBillModel.set("NoSRVorRDVCheckedError", false);
    }

    private removeFavorite(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("value");

        store.ActionsMedfarBilling.RemoveFavoriteCode(item.Code);
    }

    private changeMesure2Text(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const facCode = medfarBillModel.get("TempFacturationCode");
        facCode.Mesure2 = request.Get<string>("value");
        medfarBillModel = medfarBillModel.set("TempFacturationCode", facCode);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeMesure1Text(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const facCode = medfarBillModel.get("TempFacturationCode");
        facCode.Mesure1 = request.Get<string>("value");
        medfarBillModel = medfarBillModel.set("TempFacturationCode", facCode);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private addNewFavorite(request: LocalEvent, store: PatientStore): void {
        const code = request.Get<string>("code");
        const description = request.Get<string>("description");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("FavoritePopupOpen", false);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        store.ActionsMedfarBilling.AddNewFavoriteCode(code, description);
    }

    private cancelFavorite(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("FavoritePopupOpen", false)
            .set("FavoriteCode", "")
            .set("FavoriteDescription", "");

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private addFavorite(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("FavoritePopupOpen", true);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setPecRadio(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const code = request.Get<number>("code");

        medfarBillModel = medfarBillModel
            .set("IsForfaitAucunCodeChecked", code == 0)
            .set("Is19957CodeChecked", code == 19957)
            .set("Is19958CodeChecked", code == 19958)
            .set("Is19959CodeChecked", code == 19959)
            .set("Is19960CodeChecked", code == 19960);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private removeCommProfDoctor(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("CommProfFirstName", "")
            .set("CommProfLastName", "")
            .set("ShowCommProf", false);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setOnCommProfQuebec(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("CommProfOutQuebec", false)
            .set("CommProfQuebec", true);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setCommProfOutQuebec(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("CommProfOutQuebec", true)
            .set("CommProfQuebec", false);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeCommProfType(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const contextList = medfarBillModel.get("CommProfTypeList").toArray();

        const context = request.Get<string>("value");
        medfarBillModel = medfarBillModel.set("CommProfTypeString", context);

        contextList.map((c: ContextElementClass) => {
            if (c.ContextCode === context) {
                medfarBillModel = medfarBillModel.set("CommProfTypeSelected", c);
            }
        });

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeCommProfLastName(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("CommProfLastName", request.Get<string>("value"));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeCommProfFirstName(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("CommProfFirstName", request.Get<string>("value"));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private removeCommSpecDoctor(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("CommSpecDoctor", null)
            .set("IsCommSpecChecked", false);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeCommSpecText(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const textString = request.Get<string>("value");
        medfarBillModel = medfarBillModel.set("CommSpecSearchString", textString);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeCommSpec(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const referalItem = request.Get<PersonDropdownItem>("value");
        const previousReferalItems = medfarBillModel.get("SelectedCommSpec").toArray();

        // if selected patient is the same as the previous selected one, remove it from the list
        const list: Person[] = [];
        if (previousReferalItems && previousReferalItems.indexOf(referalItem.Item) == -1)
            list.push(referalItem.Item);

        medfarBillModel = medfarBillModel
            .set("SelectedCommSpec", Immutable.List(list))
            .set("SelectedCommSpecId", referalItem.Item.Licence);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private clearCommSpec(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("SelectedCommSpec", Immutable.List([]));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setIntClin(request: LocalEvent, store: PatientStore): void {
        let state = request.Get<MedfarBillingState>("stateDTO");
        let duration = Number(state.InterventionCliniqueDurationMin);

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const codeList = medfarBillModel.get("CodesList").toArray();

        let errors: ErrorFacturation[] = [];

        // Clean up
        this.removeCode(codeList, "AUTRES1--InterventionClinique");
        this.removeCode(codeList, "AUTRES1--InterventionClinique_supp");

        if (state.isInterventionCliniqueChecked == true && duration >= 25) {
            const autresList = medfarBillModel.get("AutresMultipleCbItems");

            // Get+Refresh CodeGroup
            const interventionClinique = autresList["AUTRES1--InterventionClinique"];
            interventionClinique.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

            // Set Context
            interventionClinique.Mesure2 = medfarBillModel.get("SelectedContextCode") == null ? "" : medfarBillModel.get("SelectedContextCode").ContextCode;
            interventionClinique.UnitLabel2 = medfarBillModel.get("SelectedContextCode") == null ? "" : medfarBillModel.get("SelectedContextCode").ContextDescription;
            interventionClinique.UnitType2 = "";

            // Insert
            codeList.unshift(interventionClinique);

            // Additional Supplément
            if (duration >= 38) {
                // Get+Refresh CodeGroup
                const interventionCliniqueSupp = autresList["AUTRES1--InterventionClinique_supp"];
                interventionCliniqueSupp.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));
                interventionCliniqueSupp.Mesure1 = (duration - 30).toString();

                interventionCliniqueSupp.Mesure2 = medfarBillModel.get("SelectedContextCode") == null ? "" : medfarBillModel.get("SelectedContextCode").ContextCode;
                interventionCliniqueSupp.UnitLabel2 = medfarBillModel.get("SelectedContextCode") == null ? "" : medfarBillModel.get("SelectedContextCode").ContextDescription;
                interventionCliniqueSupp.UnitType2 = "";

                codeList.unshift(interventionCliniqueSupp);
            }
        }

        medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codeList));
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private setPsycho(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const autresList = medfarBillModel.get("AutresMultipleCbItems");
        const duration = request.Get<number>("value");

        let errors: ErrorFacturation[] = [];

        medfarBillModel = medfarBillModel = medfarBillModel
            .set("Psycho30", duration == 30)
            .set("Psycho45", duration == 45)
            .set("Psycho60", duration == 60)
            .set("Psycho75", duration == 75)
            .set("Psycho90", duration == 90)
            .set("Psycho105", duration == 105);

        this.removeCode(codeList, "AUTRES1--InterventionPsy");
        this.removeCode(codeList, "AUTRES1--InterventionPsy_supp");

        const facturationCode = autresList["AUTRES1--InterventionPsy"];
        facturationCode.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));
        codeList.unshift(facturationCode);

        if (duration > 30) {
            const facturationCodeSupp = autresList["AUTRES1--InterventionPsy_supp"];
            facturationCodeSupp.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));
            facturationCodeSupp.Mesure1 = (duration - 30).toString();
            codeList.unshift(facturationCodeSupp);
        }

        medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private removeCode(codeList: FacturationCode[], code: string): void {
        const i = codeList.indexOfObject("CodeGroupIdentifier", code);
        if (i >= 0) {
            codeList.splice(i, 1);
        }
    }

    private changeContextAutres(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const contextList = medfarBillModel.get("ContextList").toArray();

        const contextCodeString = request.Get<string>("value");
        medfarBillModel = medfarBillModel.set("SelectedContextCodeString", contextCodeString);

        const codeList = medfarBillModel.get("CodesList").toArray();

        let context: ContextElementClass = null;
        contextList.map((c: ContextElementClass) => {
            if (c.ContextCode === contextCodeString) {
                context = c;
                medfarBillModel = medfarBillModel.set("SelectedContextCode", c);
            }
        });

        codeList.map((code: FacturationCode) => {
            if (context != null && context.ContextDescription !== "Aucun" && (code.CodeGroupIdentifier === "AUTRES1--InterventionClinique" || code.CodeGroupIdentifier === "AUTRES1--InterventionClinique_supp")) {
                code.Mesure2 = context.ContextCode;
                code.UnitLabel2 = context.ContextDescription;
                code.UnitType2 = "";
            }

            else {
                code.Mesure2 = "";
            }
        });

        medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private removeRefDoctor(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const grossesseItems = medfarBillModel.get("GrossesseItems");
        const evaluationRefereeItems = medfarBillModel.get("EvaluationRefereeItems");

        medfarBillModel = medfarBillModel.set("MedecinReferant", null);

        Object.keys(grossesseItems).map((key: string) => {
            if (grossesseItems[key].CodeGroupIdentifier === "GROSSESSE--PECavecref") {
                grossesseItems[key].IsChecked = false;
            }
        });

        Object.keys(evaluationRefereeItems).map((key: string) => {
            evaluationRefereeItems[key].IsChecked = false;
        });

        this.removeCode(codeList, "GROSSESSE--PECavecref");
        this.removeCode(codeList, "EvalRef--ProbMineur");
        this.removeCode(codeList, "EvalRef--ProbMajeur");

        medfarBillModel = medfarBillModel
            .set("CodesList", Immutable.List(codeList))
            .set("GrossesseItems", grossesseItems)
            .set("EvaluationRefereeItems", evaluationRefereeItems);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private continue(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const tempFacCode = medfarBillModel.get("TempFacturationCode");
        const codeGroupId = tempFacCode.CodeGroupIdentifier ?? "";

        const codeHelper = new FacturationCodeHelper();

        // 8859 int clini supp cannot be smaller then 15
        if (tempFacCode.Code == "8859" && (isNaN(Number(tempFacCode.Mesure1)) || Number(tempFacCode.Mesure1) < 15))
            return;

        if (medfarBillModel.get("CommProfErrorMessage") == true) {
            if (medfarBillModel.get("CommProfTypeString") == null
                || (medfarBillModel.get("CommProfOutQuebec") == false && medfarBillModel.get("CommProfQuebec") == false)
                || medfarBillModel.get("CommProfFirstName") == "" || medfarBillModel.get("CommProfFirstName") == null
                || medfarBillModel.get("CommProfLastName") == "" || medfarBillModel.get("CommProfLastName") == null) {
                return;
            }
        }

        if (medfarBillModel.get("IsDoubleEvent")) {
            const grossesseList = medfarBillModel.get("GrossesseItems");
            const csstItems = medfarBillModel.get("CSSTItems");

            switch (codeGroupId) {
                case "GROSSESSE--PECsansref": case "GROSSESSE--PECavecref": case "GROSSESSE--PEC1eTrim":
                    grossesseList[codeGroupId].IsChecked = false;
                    break;

                case "CSST--9926": case "CSST--9927": case "CSST--9928": case "CSST--9930": case "CSST--9971": case "CSST--9970": case "CSST--9900": case "CSST--9929": case "CSST--9909": case "CSST--9907": case "CSST--9944": case "CSST--9916":
                    csstItems[codeGroupId].IsChecked = false;
                    break;
            }

            medfarBillModel = medfarBillModel
                .set("GrossesseItems", grossesseList)
                .set("CSSTItems", csstItems);
        }
        else {
            const tempDateAccouchement = medfarBillModel.get("TempDatePrevuAccouchement");
            const tempDateCSST = medfarBillModel.get("TempDateCSST");

            // validate per-type requirements
            {
                if (codeGroupId.indexOf("GROSSESSE--") !== -1) {
                    if (medfarBillModel.get("DatePrevuAccouchement") == null && tempDateAccouchement == null)
                        return;

                    if (tempDateAccouchement.valueOf() >= DateTime.nowDeprecated().valueOf()) {
                        sessionStore.showPopup("La date des dernières menstruations doit être dans le passé.");
                        return;
                    }

                    tempFacCode.DateAccouchement = tempDateAccouchement;
                    medfarBillModel = medfarBillModel.set("DatePrevuAccouchement", tempDateAccouchement);
                }

                if (codeHelper.isCnesstCode(codeGroupId) || codeHelper.isCnesstCode(tempFacCode.Code)) {
                    if (tempDateCSST == null) {
                        return;
                    }
                    tempFacCode.DateCSST = tempDateCSST;
                    medfarBillModel = medfarBillModel.set("DateCSST", tempDateCSST);
                }

                if (codeGroupId === "GROSSESSE--PECavecref" || codeGroupId === "EvalRef--ProbMineur" || codeGroupId === "EvalRef--ProbMajeur") {
                    let docs = medfarBillModel.get("SelectedReferalDoctor");
                    if (medfarBillModel.get("MedecinReferant") == null && (docs == null || docs.get(0) == null || IsNullOrWhiteSpace(docs.get(0).Licence)))
                        return;

                    medfarBillModel.get("SelectedReferalDoctor").toArray().map((doc: Person) => {
                        medfarBillModel = medfarBillModel.set("MedecinReferant", doc);
                        tempFacCode.NomMedecinReferant = doc.Name;
                        tempFacCode.LicenceMedecinReferant = doc.Licence;
                    });
                }

                if (codeGroupId === "Communications--Specialiste") {
                    let docSpecs = medfarBillModel.get("SelectedCommSpec");
                    if (medfarBillModel.get("CommSpecDoctor") == null && (docSpecs == null || docSpecs.get(0) == null || IsNullOrWhiteSpace(docSpecs.get(0).Licence)))
                        return;

                    medfarBillModel.get("SelectedCommSpec").toArray().map((doc: Person) => {
                        tempFacCode.NomMedecinReferant = doc.Name;
                        tempFacCode.LicenceMedecinReferant = doc.Licence;

                        medfarBillModel = medfarBillModel.set("CommSpecDoctor", doc);
                    });
                }

                if (codeGroupId === "Communications--Profressionnel") {
                    tempFacCode.PrenomMedecinComm = medfarBillModel.get("CommProfFirstName");
                    tempFacCode.NomMedecinComm = medfarBillModel.get("CommProfLastName");
                    tempFacCode.LicenceMedecinComm = "";
                    tempFacCode.TypeMEdecinComm = medfarBillModel.get("CommProfTypeSelected").ContextCode;
                    if (medfarBillModel.get("CommProfOutQuebec"))
                        tempFacCode.ProvinceMedecinComm = "2";
                    if (medfarBillModel.get("CommProfQuebec"))
                        tempFacCode.ProvinceMedecinComm = "1";

                    medfarBillModel = medfarBillModel.set("ShowCommProf", true);
                }

                if (codeGroupId === "RDVPI--PEC") {
                    if (medfarBillModel.get("Is19957CodeChecked")) {
                        const facCode = new FacturationCode({
                            BaseCode: 19957,
                            Code: "19957",
                            CodeGroupIdentifier: "19957-Forfait",
                            Description: "Forfait PEC, Sans MD Famille, Non Vulnérable"
                        });
                        codeList.unshift(facCode);
                    }
                    if (medfarBillModel.get("Is19958CodeChecked")) {
                        const facCode = new FacturationCode({
                            BaseCode: 19958,
                            Code: "19958",
                            CodeGroupIdentifier: "19958-Forfait",
                            Description: "Forfait PEC, Sans MD Famille, Vulnérable, Non Référé"
                        });
                        codeList.unshift(facCode);
                    }
                    if (medfarBillModel.get("Is19959CodeChecked")) {
                        const facCode = new FacturationCode({
                            BaseCode: 19959,
                            Code: "19959",
                            CodeGroupIdentifier: "19959-Forfait",
                            Description: "Forfait PEC, Sans MD Famille, Vunérable, Référé"
                        });
                        codeList.unshift(facCode);
                    }
                    if (medfarBillModel.get("Is19960CodeChecked")) {
                        const facCode = new FacturationCode({
                            BaseCode: 19960,
                            Code: "19960",
                            CodeGroupIdentifier: "19959-Forfait",
                            Description: "Forfait PEC, Sans MD Famille, Très Vulnérable, Priorisé et Référé"
                        });
                        codeList.unshift(facCode);
                    }
                }
            }

            if (tempFacCode.Attribut === "C") {
                let docs = medfarBillModel.get("SelectedReferalDoctor");
                if (medfarBillModel.get("MedecinReferant") == null && IsNullOrWhiteSpace(docs?.get(0)?.Licence))
                    return;

                medfarBillModel.get("SelectedReferalDoctor").toArray().map((doc: Person) => {
                    medfarBillModel = medfarBillModel.set("MedecinReferant", doc);
                    tempFacCode.NomMedecinReferant = doc.Name;
                    tempFacCode.LicenceMedecinReferant = doc.Licence;
                });
            }

            codeList.unshift(tempFacCode);

            medfarBillModel = medfarBillModel
                .set("ReferalDoctorSearchString", "")
                .set("SelectedReferalDoctorId", "")
                .set("SelectedReferalDoctor", Immutable.List([]))

                .set("CommSpecSearchString", "")
                .set("SelectedCommSpecId", "")
                .set("SelectedCommSpec", Immutable.List([]))

                .set("CodesList", Immutable.List(codeList));
        }

        medfarBillModel = medfarBillModel.set("IsPopupInfoOpen", false);
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setCnesstToday(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("TempDateCSST", new Date());
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setPregnancyToday(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("TempDatePrevuAccouchement", new Date());
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setCnesstDate(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("TempDateCSST", request.Get<Date>("value"));
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setPregnancyDate(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const newDate = request.Get<Date>("value");

        if (newDate.valueOf() >= DateTime.nowDeprecated().valueOf()) {
            sessionStore.showPopup("La date des dernières menstruations doit être dans le passé.");
            return;
        }

        medfarBillModel = medfarBillModel.set("TempDatePrevuAccouchement", newDate);
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private clearReferrals(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("SelectedReferalDoctor", Immutable.List([]));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeReferrals(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const referalItem = request.Get<PersonDropdownItem>("value");
        const previousReferalItems = medfarBillModel.get("SelectedReferalDoctor").toArray();

        // if selected patient is the same as the previous selected one, remove it from the list
        const list: Person[] = [];
        if (previousReferalItems && previousReferalItems.indexOf(referalItem.Item) == -1)
            list.push(referalItem.Item);

        medfarBillModel = medfarBillModel
            .set("SelectedReferalDoctor", Immutable.List(list))
            .set("SelectedReferalDoctorId", referalItem.Item.Licence);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeReferralsText(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const textString = request.Get<string>("value");
        medfarBillModel = medfarBillModel.set("ReferalDoctorSearchString", textString);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private cancel(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("IsPopupInfoOpen", false);

        const rdvPatientInscritItem = medfarBillModel.get("RdvPatientInscritItem");
        const grossesseItems = medfarBillModel.get("GrossesseItems");
        const evaluationRefereeItems = medfarBillModel.get("EvaluationRefereeItems");
        const csstItems = medfarBillModel.get("CSSTItems");
        const communicationsItems = medfarBillModel.get("CommunicationsItems");

        const tmpCode = medfarBillModel.get("TempFacturationCode");
        switch (tmpCode.CodeGroupIdentifier) {
            case "RDVPI--PEC": case "RDVPI--PERVUL":
                rdvPatientInscritItem[tmpCode.CodeGroupIdentifier].IsChecked = false;
                break;

            case "GROSSESSE--PECsansref": case "GROSSESSE--PECavecref": case "GROSSESSE--PEC1eTrim":
                grossesseItems[tmpCode.CodeGroupIdentifier].IsChecked = false;
                break;

            case "EvalRef--ProbMineur": case "EvalRef--ProbMajeur":
                evaluationRefereeItems[tmpCode.CodeGroupIdentifier].IsChecked = false;

                break;

            case "CSST--9926": case "CSST--9927": case "CSST--9928": case "CSST--9930": case "CSST--9971": case "CSST--9970": case "CSST--9900": case "CSST--9929": case "CSST--9909": case "CSST--9907": case "CSST--9944": case "CSST--9916":
                csstItems[tmpCode.CodeGroupIdentifier].IsChecked = false;
                break;

            case "Communications--Specialiste": case "Communications--Profressionnel":
                communicationsItems[tmpCode.CodeGroupIdentifier].IsChecked = false;
                break;
        }

        medfarBillModel = medfarBillModel
            .set("RdvPatientInscritItem", rdvPatientInscritItem)
            .set("GrossesseItems", grossesseItems)
            .set("EvaluationRefereeItems", evaluationRefereeItems)
            .set("CSSTItems", csstItems)
            .set("CommunicationsItems", communicationsItems);

        noteModel = noteModel
            .set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setEvalReferre(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];
        const codeGroups = new FacturationCodeHelper();

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const evalList = medfarBillModel.get("EvaluationRefereeItems");

        Object.keys(evalList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                evalList[key].IsChecked = evalList[key].IsChecked != true;

                if (evalList[key].IsChecked === true) {
                    errors = evalList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    if (codeGroups.requiresReferringDoctor(key)) {
                        if (!medfarBillModel.get("MedecinReferant")) {
                            const newError = new ErrorFacturation({ Code: "RefMD", Text: "Vous devez avoir un médecin référant pour facturer cet acte." });
                            errors.push(newError);
                        }

                        medfarBillModel = medfarBillModel.set("TempFacturationCode", evalList[key]);

                        if (medfarBillModel.get("MedecinReferant")) {
                            codeList.unshift(evalList[key]);
                        }
                    }
                }
                else {
                    this.removeCode(codeList, evalList[key].CodeGroupIdentifier);
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("ErrorsFacturation", Immutable.List(errors))
            .set("CodesList", Immutable.List(codeList))
            .set("EvaluationRefereeItems", evalList);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private setCommunication(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];
        const codeGroupes = new FacturationCodeHelper();

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const commList = medfarBillModel.get("CommunicationsItems");

        Object.keys(commList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                commList[key].IsChecked = commList[key].IsChecked != true;

                if (commList[key].IsChecked) {
                    errors = commList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    if (codeGroupes.requiresProfessionalCommunication(key)) {
                        const newError = new ErrorFacturation({ Code: "CommProf", Text: "S'il vous plaît préciser le nom d'autre professionnel pour facturer cet acte." });
                        errors.push(newError);

                        medfarBillModel = medfarBillModel.set("TempFacturationCode", commList[key]);
                    }
                    if (codeGroupes.requiresSpecialistCommunication(key)) {
                        const newError = new ErrorFacturation({ Code: "CommSpec", Text: "S'il vous plaît préciser le médecin pour facturer cet acte." });
                        errors.push(newError);

                        medfarBillModel = medfarBillModel.set("TempFacturationCode", commList[key]);
                    }
                }
                else {
                    errors = [];
                    this.removeCode(codeList, commList[key].CodeGroupIdentifier);
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("ErrorsFacturation", Immutable.List(errors))
            .set("CodesList", Immutable.List(codeList))
            .set("CommunicationsItems", commList);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private setAutre(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const codeList = medfarBillModel.get("CodesList").toArray();
        const autresList = medfarBillModel.get("AutresItems");

        Object.keys(autresList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                autresList[key].IsChecked = autresList[key].IsChecked != true;

                if (autresList[key].IsChecked === true) {
                    errors = autresList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    codeList.unshift(autresList[key]);
                }

                else {
                    this.removeCode(codeList, autresList[key].CodeGroupIdentifier);
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("CodesList", Immutable.List(codeList))
            .set("AutresItems", autresList);
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setSrvNonInscrit(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const codeList = medfarBillModel.get("CodesList").toArray();
        const srvOuNonInscritList = medfarBillModel.get("SrvNonInscritItem");

        Object.keys(srvOuNonInscritList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                srvOuNonInscritList[key].IsChecked = srvOuNonInscritList[key].IsChecked != true;

                if (srvOuNonInscritList[key].IsChecked === true) {
                    errors = srvOuNonInscritList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    codeList.unshift(srvOuNonInscritList[key]);
                }
                else {
                    this.removeCode(codeList, srvOuNonInscritList[key].CodeGroupIdentifier);
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("CodesList", Immutable.List(codeList))
            .set("SrvNonInscritItem", srvOuNonInscritList);
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setVADomicile(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const codeList = medfarBillModel.get("CodesList").toArray();
        const vaDomicileList = medfarBillModel.get("DomicilesItems");

        Object.keys(vaDomicileList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                vaDomicileList[key].IsChecked = vaDomicileList[key].IsChecked != true;

                if (vaDomicileList[key].IsChecked === true) {
                    errors = vaDomicileList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    codeList.unshift(vaDomicileList[key]);
                }
                else {
                    this.removeCode(codeList, vaDomicileList[key].CodeGroupIdentifier);
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("CodesList", Immutable.List(codeList))
            .set("DomicilesItems", vaDomicileList);
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setPregnancy(request: LocalEvent, store: PatientStore): void {
        let errors: ErrorFacturation[] = [];
        const codeGroups = new FacturationCodeHelper();
        const item = request.Get<FacturationCode>("item");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const codeList = medfarBillModel.get("CodesList").toArray();
        const grossesseList = medfarBillModel.get("GrossesseItems");

        Object.keys(grossesseList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                grossesseList[key].IsChecked = grossesseList[key].IsChecked != true;

                if (grossesseList[key].IsChecked) {
                    errors = grossesseList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    if (codeGroups.requiresDateOfBirth(key)) {
                        this.bindEventDate(request.PatientId, errors, "DDM", "Vous devez avoir entré la date des dernières menstruations pour facturer cet acte.", medfarBillModel, noteModel, store, medfarBillModel.get("DatePrevuAccouchement"));
                        if (codeGroups.requiresReferringDoctor(key)) {
                            if (!medfarBillModel.get("MedecinReferant")) {
                                const newError = new ErrorFacturation({ Code: "RefMD", Text: "Vous devez avoir un médecin référant pour facturer cet acte." });
                                errors.push(newError);
                            }
                        }

                        if (medfarBillModel.get("DatePrevuAccouchement") && grossesseList[key].CodeGroupIdentifier !== "GROSSESSE--PECavecref") {
                            grossesseList[key].DateAccouchement = medfarBillModel.get("DatePrevuAccouchement");
                            codeList.unshift(grossesseList[key]);
                        }

                        medfarBillModel = medfarBillModel.set("TempFacturationCode", grossesseList[key]);
                    }
                    else {
                        codeList.unshift(grossesseList[key]);
                    }
                }

                if (grossesseList[key].IsChecked === false) {
                    this.removeCode(codeList, grossesseList[key].CodeGroupIdentifier);

                    let allGrossesseUnchecked = true;
                    const keys = Object.keys(grossesseList);

                    for (let key of keys) {
                        allGrossesseUnchecked = grossesseList[key].IsChecked === false;
                        if (!allGrossesseUnchecked) {
                            break;
                        }
                    }

                    if (allGrossesseUnchecked) {
                        medfarBillModel = medfarBillModel
                            .set("DatePrevuAccouchement", null)
                            .set("TempDatePrevuAccouchement", null);
                    }
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("CodesList", Immutable.List(codeList))
            .set("ErrorsFacturation", Immutable.List(errors))
            .set("GrossesseItems", grossesseList);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private setRdv(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const rdvList = medfarBillModel.get("RdvPatientInscritItem");

        Object.keys(rdvList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                rdvList[key].IsChecked = rdvList[key].IsChecked != true;

                if (rdvList[key].IsChecked === true) {
                    errors = rdvList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    medfarBillModel = medfarBillModel.set("TempFacturationCode", rdvList[key]);

                    switch (rdvList[key].CodeGroupIdentifier) {
                        case "RDVPI--PEC":
                            const newError = new ErrorFacturation({ Code: "PatPEC", Text: "Suggestion de facturation additionnelle avec le code facturé." });
                            errors.push(newError);

                            medfarBillModel = medfarBillModel.set("TempFacturationCode", rdvList[key]);
                            break;

                        case "RDVPI--PERVUL":
                            if (medfarBillModel.get("IsVulnerable")) {
                                codeList.unshift(rdvList[key]);
                            }
                            else {
                                const newError = new ErrorFacturation({ Code: "PatVul", Text: "Le patient doît être vulnérable pour facturer l'acte 15799" });
                                errors.push(newError);

                                medfarBillModel = medfarBillModel.set("TempFacturationCode", rdvList[key]);
                            }
                            break;

                        case "RDVPI--Suivi": case "RDVPI--PERPED":
                            codeList.unshift(rdvList[key]);
                            break;
                    }
                }
                else {
                    this.removeCode(codeList, rdvList[key].CodeGroupIdentifier);

                    if (rdvList[key].CodeGroupIdentifier === "RDVPI--PEC") {
                        this.removeCode(codeList, "19957-Forfait");
                        this.removeCode(codeList, "19958-Forfait");
                        this.removeCode(codeList, "19959-Forfait");
                        this.removeCode(codeList, "19960-Forfait");
                    }
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("CodesList", Immutable.List(codeList))
            .set("ErrorsFacturation", Immutable.List(errors))
            .set("RdvPatientInscritItem", rdvList);
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private setFavorite(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const favoriteList = medfarBillModel.get("FavoriteItems");

        Object.keys(favoriteList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                favoriteList[key].IsChecked = !favoriteList[key].IsChecked;

                if (favoriteList[key].IsChecked) {
                    errors = favoriteList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    if (!favoriteList[key].UnitLabel1) {
                        if (favoriteList[key].Attribut === "C") {
                            medfarBillModel = medfarBillModel.set("TempFacturationCode", favoriteList[key]);
                            const newError = new ErrorFacturation({ Code: "RefMD", Text: "Vous devez avoir un médecin référant pour facturer cet acte." });
                            errors.push(newError);
                        }
                        else {
                            codeList.unshift(favoriteList[key]);
                        }
                    }
                    else {
                        medfarBillModel = medfarBillModel.set("TempFacturationCode", favoriteList[key]);
                        const error = new ErrorFacturation;

                        error.Code = "Mesure1Error";
                        error.Text = "";

                        errors.push(error);
                        if (favoriteList[key].UnitLabel2) {
                            const error2 = new ErrorFacturation;

                            error2.Code = "Mesure2Error";
                            error2.Text = "";

                            errors.push(error2);
                        }

                        if (favoriteList[key].Attribut === "C") {
                            const newError = new ErrorFacturation({ Code: "RefMD", Text: "Vous devez avoir un médecin référant pour facturer cet acte." });
                            errors.push(newError);
                        }

                        medfarBillModel = medfarBillModel.set("ErrorsFacturation", Immutable.List(errors));
                    }
                }
                if (favoriteList[key].IsChecked === false) {
                    this.removeCode(codeList, favoriteList[key].CodeGroupIdentifier);
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("ErrorsFacturation", Immutable.List(errors))
            .set("FavoriteItems", favoriteList)
            .set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private setCnesst(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<FacturationCode>("item");
        let errors: ErrorFacturation[] = [];
        const codeGroupes = new FacturationCodeHelper();

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const codeList = medfarBillModel.get("CodesList").toArray();
        const csstList = medfarBillModel.get("CSSTItems");

        Object.keys(csstList).map((key: string) => {
            if (key === item.CodeGroupIdentifier) {
                csstList[key].IsChecked = csstList[key].IsChecked != true;

                if (csstList[key].IsChecked === true) {
                    errors = csstList[key].RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));

                    if (codeGroupes.isCnesstCode(key))
                        this.bindEventDate(request.PatientId, errors, "CSST", "Vous devez avoir entré une date d'événement CSST pour facturer cet acte.", medfarBillModel, noteModel, store, medfarBillModel.get("DateCSST"));

                    medfarBillModel = medfarBillModel.set("TempFacturationCode", csstList[key]);

                    if (medfarBillModel.get("DateCSST")) {
                        csstList[key].DateCSST = medfarBillModel.get("DateCSST");
                        codeList.unshift(csstList[key]);
                    }
                }
                if (csstList[key].IsChecked === false) {
                    this.removeCode(codeList, csstList[key].CodeGroupIdentifier);

                    let allCsstUnchecked = true;
                    const keys = Object.keys(csstList);

                    for (let key of keys) {
                        allCsstUnchecked = csstList[key].IsChecked === false;
                        if (!allCsstUnchecked) {
                            break;
                        }
                    }

                    if (allCsstUnchecked) {
                        medfarBillModel = medfarBillModel
                            .set("DateCSST", null)
                            .set("TempDateCSST", null);
                    }
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("ErrorsFacturation", Immutable.List(errors))
            .set("CSSTItems", csstList)
            .set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private PopInfoBox(patientId: string, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[patientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");
        const errors = medfarBillModel.get("ErrorsFacturation");

        medfarBillModel = medfarBillModel
            .set("IsDateErrorGrossesse", false)
            .set("IsDateErrorCSST", false)
            .set("IsRefDoctorError", false)
            .set("IsCommDoctorError", false)
            .set("IsCommProfessionalError", false)
            .set("IsSuggestionAdditionError", false)
            .set("IsNotVulnerableError", false)
            .set("CommSpecErrorMessage", false)
            .set("CommProfErrorMessage", false)
            .set("MustHave500PatientError", false)
            .set("PatientPECError", false)
            .set("PatientVULError", false)
            .set("Mesure1Error", false)
            .set("Mesure2Error", false)
            .set("NoSRVorRDVCheckedError", false);

        let text = "";
        errors.map((error: ErrorFacturation) => {
            if (error.Code === "DoubleEvent") {
                medfarBillModel = medfarBillModel.set("IsDoubleEvent", true);
                text += error.Text + "\n";
            }
            else {
                switch (error.Code) {
                    case "DDM":
                        medfarBillModel = medfarBillModel.set("IsDateErrorGrossesse", true);
                        text += error.Text + "\n";
                        break;

                    case "CSST":
                        medfarBillModel = medfarBillModel.set("IsDateErrorCSST", true);
                        text += error.Text + "\n";
                        break;

                    case "RefMD":
                        medfarBillModel = medfarBillModel.set("IsRefDoctorError", true);
                        text += error.Text + "\n";
                        break;

                    case "CommMDSpec":
                        medfarBillModel = medfarBillModel.set("IsCommDoctorError", true);
                        text += error.Text + "\n";
                        break;

                    case "CommMDProf":
                        medfarBillModel = medfarBillModel.set("IsCommProfessionalError", true);
                        text += error.Text + "\n";
                        break;

                    case "SuggestionAddition":
                        medfarBillModel = medfarBillModel.set("IsSuggestionAdditionError", true);
                        text += error.Text + "\n";
                        break;

                    case "IsNotVulnerableError":
                        medfarBillModel = medfarBillModel.set("IsNotVulnerableError", true);
                        text += error.Text + "\n";
                        break;

                    case "CommProf":
                        medfarBillModel = medfarBillModel.set("CommProfErrorMessage", true);
                        text += error.Text + "\n";
                        break;

                    case "CommSpec":
                        medfarBillModel = medfarBillModel.set("CommSpecErrorMessage", true);
                        text += error.Text + "\n";
                        break;

                    case "MustHave500Patient":
                        medfarBillModel = medfarBillModel.set("MustHave500PatientError", true);
                        text += error.Text + "\n";
                        break;

                    case "PatPEC":
                        medfarBillModel = medfarBillModel.set("PatientPECError", true);
                        text += error.Text + "\n";
                        break;

                    case "PatVul":
                        medfarBillModel = medfarBillModel.set("PatientVULError", true);
                        text += error.Text + "\n";
                        break;

                    case "Mesure1Error":
                        medfarBillModel = medfarBillModel.set("Mesure1Error", true);
                        break;

                    case "Mesure2Error":
                        medfarBillModel = medfarBillModel.set("Mesure2Error", true);
                        break;
                }
            }
        });

        medfarBillModel = medfarBillModel
            .set("IsPopupInfoOpen", true)
            .set("PopupInfoText", text);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[patientId] = noteModel;
    }

    private bindEventDate(patientId: string, errors: ErrorFacturation[], evenementType: string, error: string, medfarBillModel: IMedfarBillingModel, noteModel: IPatientMedicalNoteModel, store: PatientStore, evenementDate?: Date): boolean {
        const isReserved = this.isEventDateReservedByOtherThen(evenementType, medfarBillModel);
        const validDateAlreadyExist = evenementDate != null;

        // validate
        if (isReserved) {
            errors.length = 0;
            errors.push(new ErrorFacturation({ Code: "DoubleEvent", Text: "Il ne peut y avoir qu'un seul événement de type CSST ou Grossesse par facture. S.v.p créer une deuxième facture afin de séparer les événements." }));
            return false;
        }
        if (!validDateAlreadyExist) {
            errors.push(new ErrorFacturation({ Code: evenementType, Text: error }));
            return false;
        }

        // bind
        const facturationModel = medfarBillModel.get("Model");

        facturationModel.PatientDTO.DateEvenement = evenementDate;
        facturationModel.PatientDTO.TypeEvenement = evenementType;

        medfarBillModel = medfarBillModel.set("Model", facturationModel);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[patientId] = noteModel;
        return true;
    }

    private isEventDateReservedByOtherThen(currentEventType: string, medfarBillModel: IMedfarBillingModel): boolean {
        switch (currentEventType) {
            case "DDM":
                return medfarBillModel.get("DateCSST") && "DDM" === currentEventType;

            case "CSST":
                return medfarBillModel.get("DatePrevuAccouchement") && "CSST" === currentEventType;

            default:
                return false;
        }
    }

    private setMoreThan1500Patient(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("MoreThan500Patient", false)
            .set("MoreThan1000Patient", false)
            .set("MoreThan1500Patient", true);

        let errors: ErrorFacturation[] = [];

        const codeList = medfarBillModel.get("CodesList").toArray();
        if (codeList) {
            codeList.map((item: FacturationCode) => {
                item.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));
            });
        }

        medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setMoreThan1000Patient(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("MoreThan500Patient", false)
            .set("MoreThan1000Patient", true)
            .set("MoreThan1500Patient", false);

        let errors: ErrorFacturation[] = [];

        const codeList = medfarBillModel.get("CodesList").toArray();
        if (codeList) {
            codeList.map((item: FacturationCode) => {
                item.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));
            });
        }

        medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setMoreThan500Patient(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel
            .set("MoreThan500Patient", true)
            .set("MoreThan1000Patient", false)
            .set("MoreThan1500Patient", false);

        let errors: ErrorFacturation[] = [];

        const codeList = medfarBillModel.get("CodesList").toArray();
        if (codeList) {
            codeList.map((item: FacturationCode) => {
                item.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));
            });
        }

        medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setLessThan500Patient(request: LocalEvent, store: PatientStore): void {
        let code = request.Get<string>("code");
        if (code == "") return;

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const isLessThen500 = code == "< 500";

        if (code = "< 500") { // TODO: assignment in if check
            medfarBillModel = medfarBillModel
                .set("IsLessThan500", isLessThen500)
                .set("IsMoreThan500", !isLessThen500)
                .set("MoreThan500Patient", !isLessThen500);

            if (isLessThen500) {
                medfarBillModel = medfarBillModel
                    .set("MoreThan1000Patient", false)
                    .set("MoreThan1500Patient", false);
            }
        }

        let errors: ErrorFacturation[] = [];
        const codeList = medfarBillModel.get("CodesList").toArray();
        if (codeList) {
            codeList.map((item: FacturationCode) => {
                item.RefreshCode(errors, medfarBillModel.get("IsMoreThan500"), medfarBillModel.get("MoreThan1000Patient"), medfarBillModel.get("MoreThan1500Patient"), medfarBillModel.get("Is80YearsOld"), medfarBillModel.get("IsVulnerable"));
            });
        }

        medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codeList));
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private setSrv(request: LocalEvent, store: PatientStore): void {
        const code = request.Get<string>("code");
        if (code == "") return;

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const isSrv = code == "SRV"; // Not scheduled visit
        sessionStore.isScheduledVisitChecked = !isSrv;
        medfarBillModel = medfarBillModel
            .set("IsSRVChecked", isSrv)
            .set("IsRDVChecked", !isSrv);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private removeCodeFromList(request: LocalEvent, store: PatientStore): void {
        let requestValue = request.Get<FacturationCode>("value");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        let codeList = medfarBillModel.get("CodesList").toArray();
        codeList = codeList.filter(n => n.Description !== requestValue.Description).map((item: FacturationCode) => item);

        const rdvPatientInscritItem = medfarBillModel.get("RdvPatientInscritItem");
        const grossesseItems = medfarBillModel.get("GrossesseItems");
        const domicilesItems = medfarBillModel.get("DomicilesItems");
        const srvNonInscritItem = medfarBillModel.get("SrvNonInscritItem");
        const autresMultipleCbItems = medfarBillModel.get("AutresMultipleCbItems");
        const autresItems = medfarBillModel.get("AutresItems");
        const evaluationRefereeItems = medfarBillModel.get("EvaluationRefereeItems");
        const csstItems = medfarBillModel.get("CSSTItems");
        const communicationsItems = medfarBillModel.get("CommunicationsItems");
        const favoriteItems = medfarBillModel.get("FavoriteItems");

        switch (requestValue.CodeGroupIdentifier) {
            case "RDVPI--PEC": case "RDVPI--Suivi": case "RDVPI--PERVUL": case "RDVPI--PERPED":
                rdvPatientInscritItem[requestValue.CodeGroupIdentifier].IsChecked = false;

                if (requestValue.CodeGroupIdentifier === "RDVPI--PEC") {
                    // codeList = codeList.filter(n => n.Code !== "19959" || n.Code !== "19958" || n.Code !== "19959" || n.Code !== "19960").map((item: FacturationCode) => {
                    //    return item;
                    // });
                    // this.RemoveCode(codeList, "19957-Forfait");
                    // this.RemoveCode(codeList, "19958-Forfait");
                    // this.RemoveCode(codeList, "19959-Forfait");
                    // this.RemoveCode(codeList, "19960-Forfait");
                }
                break;

            case "GROSSESSE--PECsansref": case "GROSSESSE--PECavecref": case "GROSSESSE--PEC1eTrim":
                grossesseItems[requestValue.CodeGroupIdentifier].IsChecked = false;

                let allGrossesseUnchecked = true;
                let keys = Object.keys(grossesseItems);

                for (let key of keys) {
                    allGrossesseUnchecked = grossesseItems[key].IsChecked === false;
                    if (!allGrossesseUnchecked) {
                        break;
                    }
                }

                if (allGrossesseUnchecked) {
                    medfarBillModel = medfarBillModel
                        .set("DatePrevuAccouchement", null)
                        .set("TempDatePrevuAccouchement", null);
                }
                break;

            case "GROSSESSE--SuiviGrossesse":
                grossesseItems[requestValue.CodeGroupIdentifier].IsChecked = false;
                break;

            case "DOMICILE--VD1": case "DOMICILE--VD2":
                domicilesItems[requestValue.CodeGroupIdentifier].IsChecked = false;
                break;

            case "SRVPATNONINSCRIT--VPM": case "SRVPATNONINSCRIT--VPC":
                srvNonInscritItem[requestValue.CodeGroupIdentifier].IsChecked = false;
                break;

            case "AUTRES1--InterventionPsy": case "AUTRES1--InterventionPsy_supp": case "AUTRES1--InterventionClinique": case "AUTRES1--InterventionClinique_supp":
                autresMultipleCbItems[requestValue.CodeGroupIdentifier].IsChecked = false;
                break;

            case "AUTRES2--ITSS": case "AUTRES2--Interprete": case "AUTRES2--EnsGly": case "AUTRES2--ABCDaire": case "AUTRES2--SupptempGrossesse": case "AUTRES2--SuppPECtempGrossesse": case "AUTRES2--SuppPECtempGrossesseTransfert": case "AUTRES2--SuppForfaitrespvul": case "AUTRES2--SuppForfaitrespvulInscrit": case "AUTRES2--ForfaitGMFAnnuel": case "AUTRES2--SuppPECtempGrossesse1erTimestre":
                autresItems[requestValue.CodeGroupIdentifier].IsChecked = false;
                break;

            case "EvalRef--ProbMineur": case "EvalRef--ProbMajeur":
                evaluationRefereeItems[requestValue.CodeGroupIdentifier].IsChecked = false;
                break;

            case "CSST--9926": case "CSST--9927": case "CSST--9928": case "CSST--9930": case "CSST--9971": case "CSST--9970": case "CSST--9900": case "CSST--9929": case "CSST--9909": case "CSST--9907": case "CSST--9944": case "CSST--9916":
                csstItems[requestValue.CodeGroupIdentifier].IsChecked = false;

                let allCsstUnchecked = true;
                let keysCsst = Object.keys(csstItems);

                for (let key of keysCsst) {
                    allCsstUnchecked = csstItems[key].IsChecked === false;
                    if (!allCsstUnchecked) {
                        break;
                    }
                }

                if (allCsstUnchecked) {
                    medfarBillModel = medfarBillModel
                        .set("DateCSST", null)
                        .set("TempDateCSST", null);
                }

                break;

            case "Communications--Specialiste": case "Communications--Profressionnel":
                communicationsItems[requestValue.CodeGroupIdentifier].IsChecked = false;
                break;

            default:
                break;
        }

        if (requestValue.CodeGroupIdentifier
            && requestValue.CodeGroupIdentifier.indexOf("FAVORIS--") !== -1
            && favoriteItems[requestValue.CodeGroupIdentifier]) {
            favoriteItems[requestValue.CodeGroupIdentifier].IsChecked = false;
        }

        if (requestValue.Code === "19957" || requestValue.Code === "19958" || requestValue.Code === "19959" || requestValue.Code === "19960") {
            rdvPatientInscritItem["RDVPI--PEC"].IsChecked = false;
            this.removeCode(codeList, "RDVPI--PEC");
        }

        medfarBillModel = medfarBillModel
            .set("RdvPatientInscritItem", rdvPatientInscritItem)
            .set("GrossesseItems", grossesseItems)
            .set("DomicilesItems", domicilesItems)
            .set("SrvNonInscritItem", srvNonInscritItem)
            .set("AutresMultipleCbItems", autresMultipleCbItems)
            .set("AutresItems", autresItems)
            .set("EvaluationRefereeItems", evaluationRefereeItems)
            .set("CSSTItems", csstItems)
            .set("CommunicationsItems", communicationsItems)
            .set("CodesList", Immutable.List(codeList));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private addCodeToList(request: LocalEvent, store: PatientStore): void {
        const requestValue = request.Get<OtherCodeResponse>("value");
        const errors: ErrorFacturation[] = [];

        const codeHelper = new FacturationCodeHelper();

        const facturationCode = new FacturationCode({
            BaseCode: parseInt(requestValue.Code),
            Code: requestValue.Code,
            Description: requestValue.Description,
            TypeCode: requestValue.TypeCode,
            UnitLabel1: requestValue.UnitLabel1,
            UnitType1: requestValue.UnitType1,
            UnitLabel2: requestValue.UnitLabel2,
            UnitType2: requestValue.UnitType2,
            Attribut: requestValue.Attribute
        });

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        if (codeHelper.isCnesstCode(facturationCode.Code) && !this.bindEventDate(request.PatientId, errors, "CSST", "Vous devez avoir entré une date d'événement CSST pour facturer cet acte.", medfarBillModel, noteModel, store, medfarBillModel.get("DateCSST")))
            medfarBillModel = medfarBillModel.set("TempFacturationCode", facturationCode);
        else if (!facturationCode.UnitLabel1 && facturationCode.Attribut !== "C") {
            const codesList = medfarBillModel.get("CodesList").toArray();
            codesList.push(facturationCode);

            medfarBillModel = medfarBillModel.set("CodesList", Immutable.List(codesList));
        }
        else if (facturationCode.UnitLabel1) {
            medfarBillModel = medfarBillModel.set("TempFacturationCode", facturationCode);
            const error = new ErrorFacturation({ Code: "Mesure1Error", Text: "" });

            errors.push(error);
            if (facturationCode.UnitLabel2) {
                const error2 = new ErrorFacturation;

                error2.Code = "Mesure2Error";
                error2.Text = "";

                errors.push(error2);
            }
        }

        if (requestValue.Attribute === "C") {
            medfarBillModel = medfarBillModel.set("TempFacturationCode", facturationCode);
            const newError = new ErrorFacturation({ Code: "RefMD", Text: "Vous devez avoir un médecin référant pour facturer cet acte." });

            errors.push(newError);
        }

        medfarBillModel = medfarBillModel.set("ErrorsFacturation", Immutable.List(errors));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (errors.length > 0) {
            this.PopInfoBox(request.PatientId, store);
        }
    }

    private removeDiagnosisFromList(request: LocalEvent, store: PatientStore): void {
        const requestValue = request.Get<IMedfarBillingCustomCodeItem>("value");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        let diagnosisList = medfarBillModel.get("DiagnosisList").toArray();
        diagnosisList = diagnosisList
            .filter(c => c.CodeCIM !== requestValue.key)
            .map((item: DiagnosisResponse) => item);

        medfarBillModel = medfarBillModel.set("DiagnosisList", Immutable.List(diagnosisList));
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private addDiagnosisToList(request: LocalEvent, store: PatientStore): void {
        const requestValue = request.Get<DiagnosisResponse>("value");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const diagnosisList = medfarBillModel.get("DiagnosisList").toArray();
        diagnosisList.push(requestValue);

        medfarBillModel = medfarBillModel.set("DiagnosisList", Immutable.List(diagnosisList));
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeDiagnosis(request: LocalEvent, store: PatientStore): void {
        const requestValue = request.Get<string>("value");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("DiagnosisSearchText", requestValue);
        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (medfarBillModel.get("DiagnosisSearchText") && medfarBillModel.get("DiagnosisSearchText").length > 2) {
            store.ActionsMedfarBilling.SearchDiagnosis(medfarBillModel.get("DiagnosisSearchText"));
        }
    }

    private changeHours(request: LocalEvent, store: PatientStore): void {
        const requestValue = request.Get<Date>("value");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const date = medfarBillModel.get("BillDate");
        date.setHours(requestValue.getHours());
        date.setMinutes(requestValue.getMinutes());
        medfarBillModel = medfarBillModel.set("BillDate", date);

        const facturationDtoModel = medfarBillModel.get("Model");
        facturationDtoModel.DateFacturation = date;
        medfarBillModel = medfarBillModel.set("Model", facturationDtoModel);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private clickToday(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const newBillDate = new Date();
        medfarBillModel = medfarBillModel.set("BillDate", newBillDate);

        const facturationDtoModel = medfarBillModel.get("Model");
        facturationDtoModel.DateFacturation = newBillDate;
        medfarBillModel = medfarBillModel.set("Model", facturationDtoModel);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeDate(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        const newBillDate = request.Get<Date>("value");
        medfarBillModel = medfarBillModel.set("BillDate", newBillDate);

        const facturationDtoModel = medfarBillModel.get("Model");
        facturationDtoModel.DateFacturation = newBillDate;
        medfarBillModel = medfarBillModel.set("Model", facturationDtoModel);

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeComments(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let medfarBillModel = noteModel.get("MedfarBillModel");

        medfarBillModel = medfarBillModel.set("Comments", request.Get<string>("value"));

        noteModel = noteModel.set("MedfarBillModel", medfarBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private closePopup(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        noteModel = noteModel.set("ShowNewMedfarBillPopup", false);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private prepareNewBill(request: LocalEvent, store: PatientStore): void {
        const patientModel = store.PatientModels[request.PatientId];

        const requestData = {
            Patient: {
                IndividualID: patientModel.IndividualResponse.Contact_id
            },
            CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
        };

        Client.Post(ServerOperations.Server.RAMQ_CheckSubscription, requestData, response => {
            const data = new RAMQInfo().FromResponseData(response);

            const patient = store.PatientModels[request.PatientId];
            if (patient)
                patient.setRamq(data);

            store.PatientModels[request.PatientId] = patient;

            window.setTimeout(() => sessionStore.dispatch(LocalPatientOperations.MedfarBilling.CreateNewBill), 50);
        });
    }

    private createNewBill(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let patientModel = store.PatientModels[request.PatientId];

        let medfarBillModel = MyleComponentsMedfarBilling.getDefaultMedfarBillingModel();
        let medfarBillingInitialState = MyleComponentsMedfarBilling.GetDefaultState();

        const patientDto = new FacturationPatientDTO();

        patientDto.NAM = patientModel.IndividualResponse.NAM;
        patientDto.NamExp = patientModel.GetNAMExpirationDate();
        patientDto.Nom = patientModel.IndividualResponse.LastName;
        patientDto.Prenom = patientModel.IndividualResponse.FirstName;
        patientDto.DDN = patientModel.IndividualResponse.DateOfBirth;
        patientDto.Sexe = patientModel.IndividualResponse.GenderLookupId != null ? cacheStore.GetLookupById(patientModel.IndividualResponse.GenderLookupId).fr : "";

        if (IsNullOrWhiteSpace(patientDto.NAM) && (IsNullOrWhiteSpace(patientDto.Nom) || IsNullOrWhiteSpace(patientDto.Nom) || patientDto.DDN == null)) {
            sessionStore.showPopup("Le patient doit avoir au minimum un Nom/Prenom ainsi qu'une date de naissance");
            return;
        }

        patientDto.Adresse = patientModel.Addresses[0] ? patientModel.Addresses[0].StreetAddress : "";
        patientDto.Ville = patientModel.Addresses[0] ? patientModel.Addresses[0].City : "";
        patientDto.Zip = patientModel.Addresses[0] ? patientModel.Addresses[0].ZipCode : "";

        patientDto.Telephone1 = patientModel.Phones.length > 0 ? patientModel.Phones[0].Number : "";
        patientDto.Telephone2 = patientModel.Phones.length > 1 ? patientModel.Phones[1].Number : "";

        patientDto.Courriel = patientModel.Emails[0] ? patientModel.Emails[0].Address : "";
        patientDto.NoDossier = patientModel.PatientResponse.Identifier01 ? patientModel.PatientResponse.Identifier01 : "";

        const ramqInfo = patientModel.RAMQInscription;
        if (ramqInfo) {
            let representantDto = new FacturationRepresentantDTO();

            representantDto.NamResp = ramqInfo.RepresentantNam;
            representantDto.TypeResp = ramqInfo.RepresentantType;

            if (ramqInfo.RepresentantNom && ramqInfo.RepresentantPrenom) {
                representantDto.NomResp = ramqInfo.RepresentantNom + ", " + ramqInfo.RepresentantPrenom;
            }

            const vulneribilityCodesDto = ramqInfo.CaracteristiqueSantes.map(cs => {
                const vulnerabilitySingleDto = new FacturationVulnerabilityCodesDTO();

                vulnerabilitySingleDto.Code = cs.CodeSante.toString();
                vulnerabilitySingleDto.CodeDate = moment(cs.Date).toDate();

                return vulnerabilitySingleDto;
            });

            const ramqInfoDto = new FacturationRAMQinfoDTO();
            ramqInfoDto.IsGMF = ramqInfo.NoGmf && ramqInfo.NoGmf.length > 0 ? true : false;
            ramqInfoDto.MDFamilleLicenceNumber = RAMQInfo.appendRamqCheckSumIfMissing(ramqInfo.DoctorLicence);
            ramqInfoDto.DateInscrGMF = ramqInfo.DatePrisedEffet;
            ramqInfoDto.DatePriseEffetInscr = ramqInfo.DatePrisedEffet;
            ramqInfoDto.Representant = representantDto;
            ramqInfoDto.VulnerabilityCodes = vulneribilityCodesDto;
            if (ramqInfo.Lieux != null) {
                let postInscr = "";
                ramqInfo.Lieux.map((lieu: RAMQLieuDeSuivi) => {
                    postInscr += lieu.CodeLieu.toString() + ",";
                });

                ramqInfoDto.PosteInscr = postInscr.substring(0, postInscr.length - 1);
            }

            patientDto.RamqInfo = ramqInfoDto;

            medfarBillModel = medfarBillModel
                .set("IsPatientSubscribed", true)
                .set("IsPatientNotSubscribed", false);

            // window.setTimeout(() => {
            //    store.ActionsMedfarBilling.VerifyNeedForfaitGMFAnnuel(patientModel.IndividualResponse.Contact_id);
            // }, 200);
        }
        else {
            const representative = patientModel.GetRepresentative();
            if (representative != null) {
                let representantDto = new FacturationRepresentantDTO();
                representantDto.NamResp = LinkedContact.GetNAM(representative);
                representantDto.TypeResp = representative.RepresentativeType;
                representantDto.NomResp = LinkedContact.GetLastName(representative) + ", " + LinkedContact.GetFirstName(representative);

                let ramqInfoDto = new FacturationRAMQinfoDTO();
                ramqInfoDto.Representant = representantDto;
                patientDto.RamqInfo = ramqInfoDto;
            }

            medfarBillModel = medfarBillModel
                .set("IsPatientSubscribed", false)
                .set("IsPatientNotSubscribed", true);
        }

        const facturationDto = new FacturationDTO();

        const has500Patient = sessionStore.has500patient ?? true;

        medfarBillModel = medfarBillModel
            .set("IsSRVChecked", sessionStore.isScheduledVisitChecked == false)
            .set("IsRDVChecked", sessionStore.isScheduledVisitChecked == true);

        const encounter = noteModel.get("Encounter");

        facturationDto.PatientDTO = patientDto;
        facturationDto.IsSansRendezVous = !sessionStore.isScheduledVisitChecked;
        facturationDto.EmployeeId = cacheStore.GetCurrentEmployee().ContactFacet_id;
        facturationDto.EmployeeLicence = RAMQInfo.appendRamqCheckSumIfMissing(cacheStore.GetCurrentEmployee().FirstLicenceNumber);
        facturationDto.EncounterId = encounter.Id;
        facturationDto.GroupeDeFacturation = cacheStore.GetCurrentEmployee().GroupNumber;
        facturationDto.CodeDeCabinet = cacheStore.GetSiteById(encounter.SiteId).Model.ClinicalCode;
        facturationDto.FacturationRequestID = GuidHelper.NewGuid();

        medfarBillModel = medfarBillModel.set("SiteId", encounter.SiteId);

        let billDate = moment(encounter.ConsultationDate).toDate();
        let currentRelatedEmp = Encounter.GetCurrentRelatedEmployee(encounter);
        if (currentRelatedEmp != null) {
            if (!IsNullOrWhiteSpace(currentRelatedEmp.FirstContribution))
                billDate = new Date(currentRelatedEmp.FirstContribution);

            if (!IsNullOrWhiteSpace(currentRelatedEmp.DurationTicks) && currentRelatedEmp.DurationTicks != "0")
                medfarBillingInitialState.InterventionCliniqueDurationMin = TimeSpan.FromTicks(Number(currentRelatedEmp.DurationTicks)).TotalMinutes().toString();
        }

        facturationDto.DateFacturation = billDate; // 2017-10-23 Billdate is now also part of the DTO... Some Doctors were sending only comments

        medfarBillModel = medfarBillModel
            .set("BillDate", billDate)
            .set("IsVulnerable", patientDto.RamqInfo && patientDto.RamqInfo.VulnerabilityCodes && patientDto.RamqInfo.VulnerabilityCodes.length > 0)
            .set("Is80YearsOld", patientDto.DDN && patientModel.getAge() >= 80)
            .set("IsMoreThan500", has500Patient)
            .set("IsLessThan500", !has500Patient)
            .set("Model", facturationDto);
        medfarBillModel = this.initializeCodesList(medfarBillModel);
        medfarBillModel = medfarBillModel.set("IsUpdating", false);

        noteModel = noteModel
            .set("ShowNewMedfarBillPopup", true)
            .set("MedfarBillModel", medfarBillModel)
            .set("MedfarBillModelInitialState", medfarBillingInitialState);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        const isSpecialist = store.PatientMedicalNoteModels[request.PatientId].get("IsMedfarBillingSpecialist");
        store.ActionsMedfarBilling.LoadFavorite(isSpecialist);
    }

    private initializeCodesList(medfarBillModel: IMedfarBillingModel): IMedfarBillingModel {
        const facturationCode = new FacturationCodeHelper(true);

        const rdvPatientInscritItem = medfarBillModel.get("RdvPatientInscritItem");
        const grossesseItems = medfarBillModel.get("GrossesseItems");
        const domicilesItems = medfarBillModel.get("DomicilesItems");
        const srvNonInscritItem = medfarBillModel.get("SrvNonInscritItem");
        const autresMultipleCbItems = medfarBillModel.get("AutresMultipleCbItems");
        const autresItems = medfarBillModel.get("AutresItems");
        const evaluationRefereeItems = medfarBillModel.get("EvaluationRefereeItems");
        const csstItems = medfarBillModel.get("CSSTItems");
        const communicationsItems = medfarBillModel.get("CommunicationsItems");

        Object.keys(facturationCode.codeGroups).map((key: string) => {
            switch (key) {
                case "RDVPI--PEC": case "RDVPI--Suivi": case "RDVPI--PERVUL": case "RDVPI--PERPED":
                    if (Object.keys(rdvPatientInscritItem).indexOf(key) === -1) {
                        rdvPatientInscritItem[key] = new FacturationCode;
                    }
                    rdvPatientInscritItem[key] = facturationCode.codeGroups[key];
                    break;

                case "GROSSESSE--PECsansref": case "GROSSESSE--PECavecref": case "GROSSESSE--PEC1eTrim": case "GROSSESSE--SuiviGrossesse":
                    if (Object.keys(grossesseItems).indexOf(key) === -1) {
                        grossesseItems[key] = new FacturationCode;
                    }
                    grossesseItems[key] = facturationCode.codeGroups[key];
                    break;

                case "DOMICILE--VD1": case "DOMICILE--VD2":
                    if (Object.keys(domicilesItems).indexOf(key) === -1) {
                        domicilesItems[key] = new FacturationCode;
                    }
                    domicilesItems[key] = facturationCode.codeGroups[key];
                    break;

                case "SRVPATNONINSCRIT--VPM": case "SRVPATNONINSCRIT--VPC":
                    if (Object.keys(srvNonInscritItem).indexOf(key) === -1) {
                        srvNonInscritItem[key] = new FacturationCode;
                    }
                    srvNonInscritItem[key] = facturationCode.codeGroups[key];
                    break;

                case "AUTRES1--InterventionPsy": case "AUTRES1--InterventionPsy_supp": case "AUTRES1--InterventionClinique": case "AUTRES1--InterventionClinique_supp":
                    if (Object.keys(autresMultipleCbItems).indexOf(key) === -1) {
                        autresMultipleCbItems[key] = new FacturationCode;
                    }
                    autresMultipleCbItems[key] = facturationCode.codeGroups[key];
                    break;

                case "AUTRES2--ITSS": case "AUTRES2--Interprete": case "AUTRES2--EnsGly": case "AUTRES2--ABCDaire": case "AUTRES2--SupptempGrossesse": case "AUTRES2--SuppPECtempGrossesse": case "AUTRES2--SuppPECtempGrossesseTransfert": case "AUTRES2--SuppForfaitrespvul": case "AUTRES2--SuppForfaitrespvulInscrit": case "AUTRES2--ForfaitGMFAnnuel": case "AUTRES2--SuppPECtempGrossesse1erTimestre":
                    if (Object.keys(autresItems).indexOf(key) === -1) {
                        autresItems[key] = new FacturationCode;
                    }
                    autresItems[key] = facturationCode.codeGroups[key];
                    break;

                case "EvalRef--ProbMineur": case "EvalRef--ProbMajeur":
                    if (Object.keys(evaluationRefereeItems).indexOf(key) === -1) {
                        evaluationRefereeItems[key] = new FacturationCode;
                    }
                    evaluationRefereeItems[key] = facturationCode.codeGroups[key];
                    break;

                case "CSST--9926": case "CSST--9927": case "CSST--9928": case "CSST--9930": case "CSST--9971": case "CSST--9970": case "CSST--9900": case "CSST--9929": case "CSST--9909": case "CSST--9907": case "CSST--9944": case "CSST--9916":
                    if (Object.keys(csstItems).indexOf(key) === -1) {
                        csstItems[key] = new FacturationCode;
                    }
                    csstItems[key] = facturationCode.codeGroups[key];
                    break;

                case "Communications--Specialiste": case "Communications--Profressionnel":
                    if (Object.keys(communicationsItems).indexOf(key) === -1) {
                        communicationsItems[key] = new FacturationCode;
                    }
                    communicationsItems[key] = facturationCode.codeGroups[key];
                    break;
            }
        });

        return medfarBillModel
            .set("RdvPatientInscritItem", rdvPatientInscritItem)
            .set("GrossesseItems", grossesseItems)
            .set("DomicilesItems", domicilesItems)
            .set("SrvNonInscritItem", srvNonInscritItem)
            .set("AutresMultipleCbItems", autresMultipleCbItems)
            .set("AutresItems", autresItems)
            .set("EvaluationRefereeItems", evaluationRefereeItems)
            .set("CSSTItems", csstItems)
            .set("CommunicationsItems", communicationsItems);
    }
}

export { MedfarBillingHandler };
